import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    SortingState,
    RowSelectionState,
    PaginationState
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import cn from 'classnames';
import { useState } from "react";
import TableCheckBox from "../../../../../../component/common/TableCheckBox/TableCheckBox";
import UserCard from "../../../../../../component/common/UserCard/UserCard";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../../component/common/CustomButton/CustomButton";
import { ASSESTS } from "../../../../../../constant/assest";
import Pagination from "../../../../../../component/UI/Pagination/Pagination";

type UserRow = {
    id: string;
    user: string;
    avatar: string;
    daysInactive: number;
    status: "Active" | "Inactive" | "Cancelled";
    lastNotification: string;
};

const ReengagementTable = () => {

    const data: UserRow[] = [
        {
            id: "1",
            user: "Jamie Waters",
            avatar: "",
            daysInactive: 15,
            status: "Active",
            lastNotification: "1/15/2024",
        },
        {
            id: "2",
            user: "Morgan Lake",
            avatar: "",
            daysInactive: 16,
            status: "Inactive",
            lastNotification: "1/15/2024",
        },
        {
            id: "3",
            user: "Taylor Stream",
            avatar: "",
            daysInactive: 20,
            status: "Cancelled",
            lastNotification: "1/15/2024",
        },
        {
            id: "4",
            user: "Taylor Stream 2",
            avatar: "",
            daysInactive: 22,
            status: "Cancelled",
            lastNotification: "1/15/2024",
        },
        {
            id: "5",
            user: "Taylor Stream 3",
            avatar: "",
            daysInactive: 40,
            status: "Cancelled",
            lastNotification: "1/15/2024",
        },
    ];

    const columns: ColumnDef<UserRow>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <TableCheckBox
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <TableCheckBox
                    checked={row.getIsSelected()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            enableSorting: false,
        },
        {
            header: "User",
            accessorKey: "user",
            cell: ({ row }) => (
                <UserCard
                    userName={row.original.user}
                    userImage={row.original.avatar}
                />
            ),
        },
        {
            header: "Days Inactive-Logbook",
            accessorKey: "daysInactive",
        },
        {
            header: "Status",
            accessorKey: "status",
            cell: ({ getValue }) => {
                const value = getValue<string>();
                return <span className={`statusBadge ${value.toLowerCase()}`}>{value}</span>;
            },
        },
        {
            header: "Last Notification",
            accessorKey: "lastNotification",
        },
        {
            header: "Action",
            accessorKey: "action",
            cell: () => (
                <CustomButton
                    btnType={ButtonType.IMAGE}
                >
                    <Image src={ASSESTS.BELLG} alt={'Ring'} width={30} height={30} />
                </CustomButton>
            ),
            enableSorting: false,
        },
    ];

    const [sorting, setSorting] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 5,
    });

    const table = useReactTable({
        data,
        columns,
        state: { sorting, rowSelection, pagination },
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableRowSelection: true,
        autoResetPageIndex: false,
    });

    return (
        <div className={'tableContainer'}>
            <div className={'tableWrapper'}>
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isActionColumn =
                                        typeof header.column.columnDef.header === "string" &&
                                        header.column.columnDef.header === "Action";
                                    return (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                                cursor: header.column.getCanSort() ? "pointer" : "default"
                                            }}
                                            className={cn(
                                                header.column.columnDef.id && `th-${header.column.columnDef.id}`,
                                                isActionColumn ? 'text-center' : undefined
                                            )}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.getCanSort() &&
                                                <span className={cn('tableShorting')}>
                                                    <Image
                                                        src={header.column.getIsSorted() === "asc"
                                                            ? ASSESTS.ASSENDING
                                                            : header.column.getIsSorted() === "desc"
                                                                ? ASSESTS.DECENDING
                                                                : ASSESTS.SHORT}
                                                        alt="Short"
                                                    />
                                                </span>
                                            }
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => {
                                    const isActionColumn =
                                        typeof cell.column.columnDef.header === "string" &&
                                        cell.column.columnDef.header === "Action";

                                    return (
                                        <td
                                            key={cell.id}
                                            className={cn(
                                                cell.column.columnDef.id && `td-${cell.column.columnDef.id}`,
                                                isActionColumn ? 'text-center' : undefined
                                            )}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination
                enteriesShows={`${pagination.pageIndex * pagination.pageSize + 1}-${Math.min(
                    (pagination.pageIndex + 1) * pagination.pageSize,
                    data.length
                )} of ${data.length} Entries`}
                pageNumbers={Array.from({ length: table.getPageCount() }, (_, pageIndex) => (
                    <CustomButton
                        key={pageIndex}
                        onClick={() =>
                            setPagination((old) => ({ ...old, pageIndex }))
                        }
                        disabled={pagination.pageIndex === pageIndex}
                        btnType={pagination.pageIndex === pageIndex ? ButtonType.ACTIVENOSTYLE : ButtonType.NOSTYLE}
                        btnSize={ButtonSize.NORMAL}
                        btnFont={pagination.pageIndex === pageIndex ? ButtonFont.BOLD : ButtonFont.NORMAL}
                        className={cn(
                            'pageNumberBtn',
                            pagination.pageIndex === pageIndex && 'activePageNumberBtn'
                        )}
                    >
                        {pageIndex + 1}
                    </CustomButton>
                ))}
                dropDownOptions={[
                    { value: "5", label: "5" },
                    { value: "10", label: "10" },
                    { value: "20", label: "20" },
                    { value: "25", label: "25" },
                ]}
                dropdownOnChange={(value) => {
                    setPagination((old) => ({
                        ...old,
                        pageSize: Number(value),
                        pageIndex: 0,
                    }));
                }}
                dropDownPlaceholder={pagination.pageSize.toString()}
            />
        </div>
    );
};

export default ReengagementTable;
